/**
 * IS THIS SITE'S "NOW" STILL NOW? (Hamzah, 2026-10-13: "it's telling Jamshoro not reporting and
 * showing Generating".) The Overview read `/telemetry/latest` and printed it as live: four days
 * after the gateway fell silent the header still said "Generating, 2 inverters running with
 * alarms", Output now 274 kW and the energy mix 25% solar — the last readings of Oct 9, 3:54 PM —
 * while Needs a look said "Stopped reporting 4 d" two inches below. GUIDELINES §2: say which of
 * "no data" and "old data" you mean.
 *
 * Three states, all decided by the SERVER (`SiteHealth`, the reporting cadence and lumen-be
 * `telemetry/quiet-night.ts`), never by a second clock here — this only reads its answer:
 *
 *   live         reporting, or late (a few intervals behind is still the plant's present)
 *   quiet        silent only because it is night at a solar site (`lib/quiet-night.ts`): calm,
 *                no colour — the cards show the day's last numbers, labelled as such
 *   silent       stopped reporting (or never has): ONE card says so at the top, and every "now"
 *                figure says "No live reading" with its last value small beneath
 *   not-storing  readings ARRIVE and are refused (`rejecting`, migration 0040). The fault is ours,
 *                not the plant's, so the card must not send anyone to the gateway (SITES.md §7).
 *                Only when nothing is being stored — a site rejecting some payloads while others
 *                land is still live, and its figures are its present.
 *
 * Pure: the clock and the zone are parameters.
 */
import type { SiteHealth } from '@/lib/api/types';
import { isQuietAtNight } from '@/lib/quiet-night';

export type Liveness =
  | { readonly kind: 'live' }
  | { readonly kind: 'quiet'; readonly lastSeenMs: number | null }
  | { readonly kind: 'silent'; readonly lastSeenMs: number | null }
  | { readonly kind: 'not-storing'; readonly lastSeenMs: number | null; readonly reason: string | null };

const LIVE: Liveness = { kind: 'live' };

type HealthFacts = Pick<SiteHealth, 'reportingState' | 'quietAtNight' | 'lastSeenAt' | 'rejecting' | 'lastRejectionReason'>;

/** The site's state. No health yet (first fetch in flight) reads as live: nothing is claimed. */
export function livenessOf(h: HealthFacts | null | undefined): Liveness {
  if (!h) return LIVE;
  const parsed = h.lastSeenAt ? Date.parse(h.lastSeenAt) : Number.NaN;
  const lastSeenMs = Number.isFinite(parsed) ? parsed : null;
  const stored = h.reportingState === 'reporting' || h.reportingState === 'late';
  if (h.rejecting === true && !stored) {
    return { kind: 'not-storing', lastSeenMs, reason: h.lastRejectionReason ?? null };
  }
  if (stored) return LIVE;
  if (isQuietAtNight(h)) return { kind: 'quiet', lastSeenMs };
  return { kind: 'silent', lastSeenMs };
}

/** True when the page's "now" figures are old readings. */
export function isStale(l: Liveness): boolean {
  return l.kind !== 'live';
}

/* ═══════════════════════════════════════════════════════════════════ words ══ */

/** "3:54 PM" on the site's own day, else "Oct 9, 3:54 PM" — the site's clock, 12-hour. */
export function whenWords(ms: number, nowMs: number, timeZone?: string): string {
  const zone = timeZone ? { timeZone } : {};
  const day = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', ...zone });
  const time = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, ...zone }).format(ms);
  if (day.format(ms) === day.format(nowMs)) return time;
  const date = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', ...zone }).format(ms);
  return `${date}, ${time}`;
}

/**
 * HOW LONG A SILENCE HAS RUN — the one age every not-reporting word uses (the card, the Sites list,
 * a device row, the telemetry line), so two of them can never disagree. Never rounds UP: a gap of
 * 3 days 18 hours read "(4 days)" on the card and "3d" on the Sites list (2026-10-13). Two units,
 * floored: "<1m", "40m", "5h 12m", "3d 18h"; a round figure drops its zero part ("2h", "3d").
 */
export function ageWords(ms: number): string {
  const totalMin = Math.floor(Math.max(0, ms) / 60_000);
  if (totalMin < 1) return '<1m';
  if (totalMin < 60) return `${totalMin}m`;
  const totalH = Math.floor(totalMin / 60);
  if (totalH < 24) {
    const m = totalMin % 60;
    return m > 0 ? `${totalH}h ${m}m` : `${totalH}h`;
  }
  const d = Math.floor(totalH / 24);
  const h = totalH % 24;
  return h > 0 ? `${d}d ${h}h` : `${d}d`;
}

/** The top card's headline: "Jamshoro not reporting since Oct 9, 3:54 PM (4 days)". */
export function silenceHeadline(site: string, l: Liveness, nowMs: number, timeZone?: string): string | null {
  if (l.kind === 'silent') {
    if (l.lastSeenMs === null) return `${site} has not reported yet`;
    return `${site} not reporting since ${whenWords(l.lastSeenMs, nowMs, timeZone)} (${ageWords(nowMs - l.lastSeenMs)})`;
  }
  if (l.kind === 'not-storing') {
    return l.lastSeenMs === null
      ? `${site}'s readings arrive and are not being stored`
      : `${site}'s readings arrive and nothing has been stored since ${whenWords(l.lastSeenMs, nowMs, timeZone)}`;
  }
  return null;
}

/**
 * The card's one short line (Hamzah's rule: no long descriptive text, the rest behind an ⓘ). The
 * full advice is `silenceAdvice`, which the card's ⓘ carries.
 */
export function silenceShort(l: Liveness, scope: 'site' | 'device' = 'site'): string | null {
  if (l.kind === 'silent') return scope === 'device' ? 'Check the device’s power and its link.' : 'Check the gateway’s power and internet.';
  if (l.kind === 'not-storing') return 'This is on our side, not the site’s.';
  return null;
}

/**
 * What to do about it — at the plant for a silence, on our side for a refusal. A DEVICE silent while
 * the rest of its site reports points at the device, not the gateway: the gateway is plainly working.
 */
export function silenceAdvice(l: Liveness, scope: 'site' | 'device' = 'site'): string | null {
  if (scope === 'device' && l.kind === 'silent') {
    return l.lastSeenMs === null
      ? 'Nothing has arrived from this device, while the rest of the site reports. Check it is wired to the gateway and configured to be read.'
      : 'The rest of the site is reporting, so the gateway is working. Check this device: its power, and its link to the gateway.';
  }
  if (l.kind === 'silent') {
    return l.lastSeenMs === null
      ? 'Nothing has arrived from the site. Check its gateway is installed, powered and online.'
      : 'Nothing has arrived from the site since then. Check the gateway at the site: its power, its internet link, then the gateway itself.';
  }
  if (l.kind === 'not-storing') {
    return 'The site is sending; Lumen is refusing what arrives. This is on our side, not the site’s, so nobody needs to go to the gateway.';
  }
  return null;
}

/** One line under a figure that is now an old reading: "last reading 11:29 PM", "last Oct 9, 3:54 PM". */
export function lastReadingWords(l: Liveness, nowMs: number, timeZone?: string): string {
  if (l.kind === 'live' || l.lastSeenMs === null) return 'no reading yet';
  return `last reading ${whenWords(l.lastSeenMs, nowMs, timeZone)}`;
}

/**
 * The last value of a "now" figure, small under its word: "last 274 kW, Oct 9, 3:54 PM". `shown` is
 * the value already formatted with its unit, or `null` to say only when ("last reading 11:29 PM") —
 * a quiet night passes `null`, since the 0 kW read in the dark says nothing worth repeating.
 */
export function lastValueWords(l: Liveness, shown: string | null, nowMs: number, timeZone?: string): string {
  if (l.kind === 'live' || l.lastSeenMs === null) return 'no reading yet';
  const when = whenWords(l.lastSeenMs, nowMs, timeZone);
  return shown === null ? `last reading ${when}` : `last ${shown}, ${when}`;
}

/** Why a "now" card shows a word — its ⓘ, in one sentence. */
export function staleWhy(l: Liveness): string | null {
  if (l.kind === 'quiet') return 'The site’s gateway rests at night; nothing is read until it wakes, so the last reading is shown.';
  if (l.kind === 'silent') return 'Nothing has arrived from the site, so nothing can be read as now. The last reading is shown beneath.';
  if (l.kind === 'not-storing') return 'Readings arrive and are refused on our side, so none is shown as now. The last stored one is beneath.';
  return null;
}

/**
 * The word a "now" card shows in place of its figure — "Quiet at night" (calm) or "No live reading"
 * (the top card carries the problem). `null` while live: the card shows its figure.
 */
export function staleWord(l: Liveness): string | null {
  if (l.kind === 'quiet') return 'Quiet at night';
  if (l.kind === 'silent' || l.kind === 'not-storing') return 'No live reading';
  return null;
}

/** The header pill in place of the plant's state — the plant ladder reads registers that are now old. */
export function stalePill(
  l: Liveness,
  nowMs: number,
  timeZone?: string,
): { text: string; tone: 'muted' | 'danger'; why: string } | null {
  if (l.kind === 'quiet') {
    return { text: 'Quiet at night', tone: 'muted', why: `The site's gateway rests at night; ${lastReadingWords(l, nowMs, timeZone)}.` };
  }
  if (l.kind === 'silent') {
    return {
      text: 'Not reporting',
      tone: 'danger',
      why: l.lastSeenMs === null ? 'Nothing has arrived from the site yet.' : `Nothing has arrived since ${whenWords(l.lastSeenMs, nowMs, timeZone)}.`,
    };
  }
  if (l.kind === 'not-storing') {
    return { text: 'Receiving, not storing', tone: 'danger', why: l.reason ?? 'Readings arrive and are refused on our side.' };
  }
  return null;
}

/**
 * The silent sites a fleet's card lists — quiet-at-night sites are left out (night is not a
 * fault), so are live ones. Worst first: the longest silence leads, never-reported last.
 */
export function silentSites(
  sites: readonly (HealthFacts & Pick<SiteHealth, 'siteId' | 'siteName'>)[],
): { siteId: string; siteName: string; liveness: Liveness }[] {
  const out = sites
    .map((s) => ({ siteId: s.siteId, siteName: s.siteName, liveness: livenessOf(s) }))
    .filter((s) => s.liveness.kind === 'silent' || s.liveness.kind === 'not-storing');
  const at = (l: Liveness): number => (l.kind === 'live' ? Number.POSITIVE_INFINITY : (l.lastSeenMs ?? Number.POSITIVE_INFINITY));
  return out.sort((a, b) => at(a.liveness) - at(b.liveness));
}

/**
 * One DEVICE's state on its own page. The site's state wins whenever the site is not live — a silent
 * gateway silences every device behind it, and night is night for all of them. Otherwise the device
 * answers for itself from its own readings' age (`state`, the page's reporting cadence) and its
 * freshest reading's time. `null` state is a device that has never reported.
 */
export function deviceLivenessOf(
  site: Liveness,
  state: 'reporting' | 'late' | 'silent' | null,
  lastReadMs: number | null,
): Liveness {
  if (site.kind !== 'live') return site;
  if (state === 'reporting' || state === 'late') return LIVE;
  return { kind: 'silent', lastSeenMs: state === null ? null : lastReadMs };
}

/**
 * Everything the not-reporting card says about one subject: the headline, the advice for its scope,
 * and the note about the figures beneath. `null` while live or quiet — night never gets the card.
 */
export function silenceCard(
  name: string,
  l: Liveness,
  scope: 'site' | 'device',
  nowMs: number,
  timeZone?: string,
): { headline: string; short: string; more: string } | null {
  const headline = silenceHeadline(name, l, nowMs, timeZone);
  const short = silenceShort(l, scope);
  const advice = silenceAdvice(l, scope);
  if (headline === null || short === null || advice === null) return null;
  return { headline, short, more: `${advice} The figures below are its last readings, each marked with when it was read.` };
}

/**
 * A list's verdict while its site is not live — a devices table, a fleet of machines. Every row is
 * silent BECAUSE the site is, so counting them as N silent devices ("0 of 12 fine, 12 silent", red,
 * all night) blames the machines for the gateway, or for the night. `null` while live: the list
 * counts its rows as it always has.
 */
export function staleListVerdict(
  l: Liveness,
  total: number,
  noun: string,
  nowMs: number,
  timeZone?: string,
): { text: string; tone: 'na' | 'fail' } | null {
  const many = `${noun}${total === 1 ? '' : 's'}`;
  if (l.kind === 'quiet') {
    return { text: `All ${total} ${many} quiet for the night, ${lastReadingWords(l, nowMs, timeZone)}.`, tone: 'na' };
  }
  if (l.kind === 'silent' || l.kind === 'not-storing') {
    const why = l.kind === 'silent' ? 'the site is not reporting' : 'its readings are not being stored';
    return { text: `None of ${total} ${many} can be read now: ${why}.`, tone: 'fail' };
  }
  return null;
}

/**
 * The short status word for a list or a picker: "Not reporting since Oct 9" — the day alone when it
 * was another day, the time when it was today ("since 8:12 AM"); "Not reporting" with no reading
 * at all. `null` while live or quiet: those keep their own calm words.
 */
export function notReportingShort(l: Liveness, nowMs: number, timeZone?: string): string | null {
  if (l.kind !== 'silent' && l.kind !== 'not-storing') return null;
  const lead = l.kind === 'not-storing' ? 'Not storing' : 'Not reporting';
  if (l.lastSeenMs === null) return lead;
  const zone = timeZone ? { timeZone } : {};
  const day = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', ...zone });
  const when =
    day.format(l.lastSeenMs) === day.format(nowMs)
      ? new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, ...zone }).format(l.lastSeenMs)
      : new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', ...zone }).format(l.lastSeenMs);
  return `${lead} since ${when}`;
}

/** The explorer's one line while the site is not live: "No new readings since Oct 9, 3:54 PM." */
export function noNewReadingsLine(l: Liveness, nowMs: number, timeZone?: string): string | null {
  if (l.kind === 'live') return null;
  if (l.lastSeenMs === null) return 'No readings from this site yet.';
  const when = whenWords(l.lastSeenMs, nowMs, timeZone);
  const age = ageWords(nowMs - l.lastSeenMs);
  if (l.kind === 'quiet') return `Quiet at night: no new readings since ${when}.`;
  if (l.kind === 'not-storing') return `No new readings stored since ${when} (${age}): they arrive and are refused on our side.`;
  return `No new readings since ${when} (${age}).`;
}

/**
 * The small notice beside a diagram that paints the site's latest values (SLD Studio, the site's
 * single-line diagram): "Not reporting: values from the last reading, Oct 9, 3:54 PM", or the calm
 * "Quiet at night: …". `null` while live — the values are now.
 */
export function staleValuesNotice(l: Liveness, nowMs: number, timeZone?: string): string | null {
  if (l.kind === 'live') return null;
  const lead = l.kind === 'quiet' ? 'Quiet at night' : l.kind === 'not-storing' ? 'Not storing' : 'Not reporting';
  if (l.lastSeenMs === null) return `${lead}: no reading has arrived yet`;
  return `${lead}: values from the last reading, ${whenWords(l.lastSeenMs, nowMs, timeZone)}`;
}

/* ════════════════════════════════════════════ the screen, the card, or nothing ══ */

/**
 * WHAT A PAGE SHOWS WHILE ITS SITE IS NOT REPORTING (Hamzah, 2026-10-13: a card on top PLUS every
 * figure saying "No live reading" was a wall of the same news; he wants one centred screen, the way
 * a crashed view gets one). Per page:
 *
 *   'live'     pages whose job is "now" — Overview, a site's Diagrams, Devices, a plant, a device.
 *              Silent or not storing: the whole content gives way to ONE screen, unless the reader
 *              chose "Show last readings" for this site this session, when the page comes back in
 *              its stale form under a slim card with "Hide".
 *   'history'  pages about the past or the setup — Alarms, Analytics, Compliance parameters,
 *              Details, Readings, Compare. Their content is still true; a slim card says why
 *              nothing new is arriving, and no screen.
 *
 * Quiet at night is NEITHER: the calm page with the day's last numbers. Live: nothing.
 */
export type SilenceTreatment = 'screen' | 'card' | 'none';

export function silenceTreatment(l: Liveness, page: 'live' | 'history', revealed: boolean): SilenceTreatment {
  if (l.kind !== 'silent' && l.kind !== 'not-storing') return 'none';
  if (page === 'history') return 'card';
  return revealed ? 'card' : 'screen';
}

/**
 * The Overview over a FLEET: the screen only when EVERY site in view is silent or not storing —
 * one silent site among working ones keeps the normal page with the compact card listing it.
 * Quiet sites are not silent. An empty fleet shows nothing.
 */
export function fleetSilenceTreatment(ls: readonly Liveness[], revealed: boolean): SilenceTreatment {
  const silent = ls.filter((l) => l.kind === 'silent' || l.kind === 'not-storing').length;
  if (silent === 0) return 'none';
  if (silent < ls.length) return 'card';
  return revealed ? 'card' : 'screen';
}

/** The screen's words: a headline, one line, and the rest for its ⓘ. `null` when no screen applies. */
export function silenceScreenWords(
  name: string,
  l: Liveness,
  nowMs: number,
  timeZone?: string,
): { headline: string; line: string; more: string } | null {
  if (l.kind === 'silent') {
    if (l.lastSeenMs === null) {
      return {
        headline: `${name} has not reported yet`,
        line: 'Nothing has arrived from the site. Check its gateway is installed, powered and online.',
        more: 'A site starts reporting once its gateway publishes to the broker. Until then there is nothing to show here.',
      };
    }
    const when = whenWords(l.lastSeenMs, nowMs, timeZone);
    return {
      headline: `${name} is not reporting`,
      line: `Nothing received since ${when} (${ageWords(nowMs - l.lastSeenMs)}). Check the gateway’s power and internet.`,
      more: `${silenceAdvice(l) ?? ''} Show last readings to see the page as it was at ${when}, each figure marked with when it was read.`,
    };
  }
  if (l.kind === 'not-storing') {
    const since = l.lastSeenMs === null ? null : whenWords(l.lastSeenMs, nowMs, timeZone);
    return {
      headline: 'Readings are arriving but not being stored',
      line: `${since ? `Nothing stored for ${name} since ${since}. ` : `Nothing is being stored for ${name}. `}This is on our side, not the site’s.`,
      more: `The site is sending; Lumen refuses what arrives${l.reason ? ` (${l.reason})` : ''}. Nobody needs to go to the gateway. Feed health under Integrations shows each refusal and its reason.`,
    };
  }
  return null;
}

/** The fleet screen's words, when no site in view is reporting. */
export function fleetSilenceScreenWords(count: number): { headline: string; line: string; more: string } {
  return {
    headline: count === 1 ? 'The site in view is not reporting' : `None of the ${count} sites is reporting`,
    line: 'Nothing is arriving from any of them. Check each gateway’s power and internet.',
    more: 'When every site is silent at once, check the shared link first: the broker, or the network the gateways reach it over. Show last readings lists each site and when it was last heard.',
  };
}

/* ── "Show last readings", remembered per site for the session ──────────────────────────── */

const REVEAL_PREFIX = 'lumen.silence.reveal.';

/** A sessionStorage-shaped store, so the memory can be tested and survive a storage that throws. */
export interface RevealStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Whether the reader chose to see this site's last readings this session. A throwing or absent
 *  store (private window, blocked site data) reads as not chosen: the screen is the safe default. */
export function readRevealed(store: RevealStore | null | undefined, key: string): boolean {
  try {
    return store?.getItem(REVEAL_PREFIX + key) === '1';
  } catch {
    return false;
  }
}

/** Remember, or forget, the choice. A store that throws keeps the choice for this page view only. */
export function writeRevealed(store: RevealStore | null | undefined, key: string, on: boolean): void {
  try {
    if (on) store?.setItem(REVEAL_PREFIX + key, '1');
    else store?.removeItem(REVEAL_PREFIX + key);
  } catch {
    /* Storage refused (private mode, quota): the in-memory state still answers this view. */
  }
}

/* ══════════════════════════════════════ a history page over a period with nothing in it ══ */

/**
 * SITE → ANALYTICS WHILE THE SITE IS SILENT (Hamzah, 2026-10-13: the slim card over a page of "–" and
 * "No readings in this period"). Analytics is about the past, so the screen applies only when the
 * CHOSEN PERIOD cannot have a reading because of the silence — it starts after the last one ("13 Oct,
 * Day" on Oct 13 with nothing since Oct 9). A period that holds readings (a past day, a week across
 * Oct 7 to 9, Lifetime) keeps the page under the slim card. Night and live: nothing. A site that
 * never reported has no period with readings, so the screen, without a day to offer.
 *
 * `fromMs` is the period's first instant, `null` for an unbounded one (Lifetime).
 */
export function periodSilence(l: Liveness, fromMs: number | null): SilenceTreatment {
  if (l.kind !== 'silent' && l.kind !== 'not-storing') return 'none';
  if (l.lastSeenMs === null) return 'screen';
  if (fromMs === null) return 'card';
  return fromMs > l.lastSeenMs ? 'screen' : 'card';
}

/** The last day that holds readings, on the site's clock — "Show Oct 9" sets the picker to it. The
    label is month first, as every other date on these pages is ("Oct 9, 3:54 PM"). */
export function lastDataDay(
  l: Liveness,
  timeZone?: string,
): { year: number; month: number; day: number; label: string } | null {
  if (l.kind === 'live' || l.lastSeenMs === null) return null;
  const zone = timeZone ? { timeZone } : {};
  const parts = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', ...zone }).formatToParts(l.lastSeenMs);
  const n = (t: string): number => Number(parts.find((p) => p.type === t)?.value ?? Number.NaN);
  const label = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short', ...zone }).format(l.lastSeenMs);
  return { year: n('year'), month: n('month'), day: n('day'), label };
}

/**
 * THE PAGE'S CLOCK ONCE THE READER CHOSE "SHOW LAST READINGS" (Hamzah, 2026-10-14).
 *
 * The button used to give the page back as it is NOW — "Power today" empty, "– kWh today", every
 * plant "–" — because a site silent since Oct 9 has nothing today. What the reader asked to see is
 * the last day that HAS readings. So a page that chose it reads its clock as the moment of the last
 * reading: every "today" it computes (the curve, the kWh, the plants' yield, a device's last 24
 * hours) becomes Oct 9 up to 3:54 PM, through the code that already draws "today", with no second
 * path to drift from it. One minute past the reading, so the bucket that holds it is inside the
 * window.
 *
 * `null` when there is nothing to move to — the site is live or quiet, or never reported — and the
 * page keeps its own clock.
 */
export function lastReadingClock(l: Liveness): number | null {
  if (l.kind !== 'silent' && l.kind !== 'not-storing') return null;
  return l.lastSeenMs === null ? null : l.lastSeenMs + 60_000;
}

/** The clock a page reads by: the live one, or the last reading's once "Show last readings" is on. */
export function pageClock(l: Liveness, revealed: boolean, liveNowMs: number): number {
  const last = revealed ? lastReadingClock(l) : null;
  return last ?? liveNowMs;
}

/** The screen's line over an empty period: why there is nothing, and since when. */
export function periodSilenceWords(
  name: string,
  l: Liveness,
  nowMs: number,
  timeZone?: string,
): { headline: string; line: string; more: string } | null {
  const base = silenceScreenWords(name, l, nowMs, timeZone);
  if (!base || l.kind === 'live' || l.lastSeenMs === null) return base;
  const when = whenWords(l.lastSeenMs, nowMs, timeZone);
  return {
    headline: base.headline,
    line:
      l.kind === 'not-storing'
        ? `Nothing stored since ${when}, so this period has no readings. This is on our side, not the site’s.`
        : `Nothing received since ${when} (${ageWords(nowMs - l.lastSeenMs)}), so this period has no readings.`,
    more: `${silenceAdvice(l) ?? ''} Any period up to ${when} still shows what the plant did.`,
  };
}
